import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, InformationCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

interface CsvScoreImportGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CsvScoreImportGuide({ isOpen, onClose }: CsvScoreImportGuideProps) {
  const [activeTab, setActiveTab] = useState('format');

  const downloadTemplate = () => {
    const headers = ['player_id', 'round', 'score', 'date', 'notes'];
    const csvContent = headers.join(',');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'score_import_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-3xl">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-golf-orange/10 rounded-full p-3">
                      <InformationCircleIcon className="h-6 w-6 text-golf-orange" />
                    </div>
                    <Dialog.Title as="h3" className="ml-3 text-xl font-medium text-gray-900">
                      CSV Score Import Guide
                    </Dialog.Title>
                  </div>
                </div>

                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px">
                    <button
                      className={`px-6 py-3 border-b-2 text-sm font-medium ${
                        activeTab === 'format'
                          ? 'border-golf-orange text-golf-orange'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('format')}
                    >
                      File Format
                    </button>
                    <button
                      className={`px-6 py-3 border-b-2 text-sm font-medium ${
                        activeTab === 'example'
                          ? 'border-golf-orange text-golf-orange'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('example')}
                    >
                      Example
                    </button>
                    <button
                      className={`px-6 py-3 border-b-2 text-sm font-medium ${
                        activeTab === 'tips'
                          ? 'border-golf-orange text-golf-orange'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('tips')}
                    >
                      Tips & Troubleshooting
                    </button>
                  </nav>
                </div>

                <div className="px-6 py-4">
                  {activeTab === 'format' && (
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Your CSV file must include the following columns in the header row:
                      </p>
                      
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Required Columns:</h4>
                        <ul className="list-disc pl-5 space-y-2">
                          <li className="text-gray-600">
                            <span className="font-mono bg-gray-100 px-1 rounded">player_id</span> - 
                            The UUID of the player from the database (e.g., 123e4567-e89b-12d3-a456-426614174000)
                          </li>
                          <li className="text-gray-600">
                            <span className="font-mono bg-gray-100 px-1 rounded">round</span> - 
                            The round number (e.g., 1, 2, 3, 4)
                          </li>
                          <li className="text-gray-600">
                            <span className="font-mono bg-gray-100 px-1 rounded">score</span> - 
                            The player's score relative to par (e.g., -2, 0, 3)
                          </li>
                          <li className="text-gray-600">
                            <span className="font-mono bg-gray-100 px-1 rounded">date</span> - 
                            The date of the round in YYYY-MM-DD format (e.g., 2025-06-07)
                          </li>
                        </ul>
                        
                        <h4 className="font-medium text-gray-700 mt-4 mb-2">Optional Columns:</h4>
                        <ul className="list-disc pl-5">
                          <li className="text-gray-600">
                            <span className="font-mono bg-gray-100 px-1 rounded">notes</span> - 
                            Any additional notes about the round
                          </li>
                        </ul>
                      </div>
                      
                      <div className="flex justify-center mt-4">
                        <button
                          onClick={downloadTemplate}
                          className="flex items-center gap-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90"
                        >
                          <DocumentTextIcon className="w-5 h-5" />
                          <span>Download Template</span>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'example' && (
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Here's an example of a properly formatted CSV file for importing scores:
                      </p>
                      
                      <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
                        <pre className="text-sm text-gray-700 font-mono">
                          player_id,round,score,date,notes<br/>
                          123e4567-e89b-12d3-a456-426614174000,1,-2,2025-06-07,Great front nine<br/>
                          123e4567-e89b-12d3-a456-426614174000,2,1,2025-06-08,Struggled with putting<br/>
                          234e5678-e89b-12d3-a456-426614174000,1,0,2025-06-07,Consistent play<br/>
                          234e5678-e89b-12d3-a456-426614174000,2,-1,2025-06-08,Eagle on hole 12
                        </pre>
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-700 flex items-center gap-2 mb-2">
                          <InformationCircleIcon className="w-5 h-5" />
                          <span>Important Notes:</span>
                        </h4>
                        <ul className="list-disc pl-5 text-blue-600 space-y-1">
                          <li>The first row must contain the column headers</li>
                          <li>Each subsequent row represents one score record</li>
                          <li>Ensure there are no extra spaces before or after values</li>
                          <li>For scores, use negative numbers for under par (e.g., -2), 0 for even par, and positive numbers for over par (e.g., 3)</li>
                          <li>The system will validate each record before importing</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'tips' && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-700 mb-2">Common Issues and Solutions:</h4>
                      
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-1">Invalid Player IDs</h5>
                          <p className="text-gray-600 text-sm">
                            Ensure that all player_id values are valid UUIDs that exist in the players table. You can export player data from the Players section to get the correct IDs.
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-1">Duplicate Scores</h5>
                          <p className="text-gray-600 text-sm">
                            The system will not import scores for player/round combinations that already exist. If you need to update existing scores, use the edit function instead.
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-1">Date Format Issues</h5>
                          <p className="text-gray-600 text-sm">
                            Dates must be in YYYY-MM-DD format (e.g., 2025-06-07). Other formats will cause import errors.
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-1">CSV Encoding</h5>
                          <p className="text-gray-600 text-sm">
                            Save your CSV file with UTF-8 encoding to ensure special characters are handled correctly.
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h5 className="font-medium text-gray-700 mb-1">Large Files</h5>
                          <p className="text-gray-600 text-sm">
                            For very large imports (hundreds of scores), consider splitting your CSV into smaller batches to improve reliability.
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg mt-4">
                        <h4 className="font-medium text-green-700 flex items-center gap-2 mb-2">
                          <InformationCircleIcon className="w-5 h-5" />
                          <span>Pro Tip:</span>
                        </h4>
                        <p className="text-green-600">
                          You can prepare your CSV file in Excel or Google Sheets and then export it as CSV. Just make sure the column names match exactly what's required.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90"
                    onClick={onClose}
                  >
                    Got it
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}